/* Roteamento interno do painel.

   O projeto de origem usava react-router-dom v7 (Routes/Navigate/useNavigate).
   O AVA está no v5, então aqui é Switch/Route/Redirect. Os paths são absolutos
   a partir de IE_PATH porque a rota-mãe é registrada sem `exact`.

   O guard não é decoração: sem ele, um usuário com papel de professor
   alcançaria /inteligencia-educacional/gestor digitando a URL. A checagem é
   pelo perfil do PAPEL ATIVO (D3). O ProtectedRoute (em InteligenciaEducacional.jsx)
   só decide se o papel abre a seção; qual painel ele vê é decidido aqui.

   Não há mais tela de seleção de visão: o painel abre no perfil do papel ativo
   e é o único acessível. Ver a nota em config/perfilPorRole.js. */

import React from "react";

import PropTypes from "prop-types";
import { Redirect, Route, Switch } from "react-router-dom";

import PainelShell from "./components/PainelShell";
import { EmptyState } from "./components/ui/primitives";
import { usePerfil } from "./context/PerfilContext";
import { AdminApp } from "./pages/admin/AdminApp";
import { GestorApp } from "./pages/gestor/GestorApp";
import { ProfessorApp } from "./pages/professor/ProfessorApp";
import { IE_PATH } from "../../config/inteligenciaEducacional";

/* O guard compara com o perfil do PAPEL ATIVO, não com a lista de perfis que os
   papéis do usuário alcançam. A diferença é de permissão: antes, quem era
   professor e também administrador escolar abria /gestor ou /admin digitando a
   URL, porque a lista somava os dois papéis. Agora o papel ativo é o limite. */
function Guard({ precisa, children }) {
  const { perfil } = usePerfil();
  if (perfil !== precisa) return <Redirect to={IE_PATH} />;
  return children;
}

Guard.propTypes = {
  precisa: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};

export default function App() {
  const { perfil } = usePerfil();

  return (
    <Switch>
      <Route exact path={IE_PATH}>
        {/* Entra direto no perfil do papel ativo — sempre, sem escolha. O
            fallback não é decoração: um papel liberado no access_roles_links
            mas sem perfil em perfilPorRole chega aqui, e dizer o motivo é
            melhor que um redirect que pisca. */}
        {perfil ? (
          <Redirect to={`${IE_PATH}/${perfil}`} />
        ) : (
          <PainelShell>
            <EmptyState
              title="Este perfil não tem acesso à Inteligência Educacional"
              hint="O painel abre conforme o perfil com que você está no AVA."
            />
          </PainelShell>
        )}
      </Route>

      <Route path={`${IE_PATH}/professor`}>
        <Guard precisa="professor">
          <ProfessorApp />
        </Guard>
      </Route>

      <Route path={`${IE_PATH}/admin`}>
        <Guard precisa="admin">
          <AdminApp />
        </Guard>
      </Route>

      <Route path={`${IE_PATH}/gestor`}>
        <Guard precisa="gestor">
          <GestorApp />
        </Guard>
      </Route>

      <Redirect to={IE_PATH} />
    </Switch>
  );
}
